// Ачивки: что игрок сделал за партию. Считаются по записи партии (сид + журнал) — реплеем с нуля, по событиям
// движка: так их не подделать, и любую старую партию можно пересчитать. Источник истины — сервер: он
// проверяет хотсит-запись реплеем (verifyRecord) и заодно получает ачивки, онлайн-партии считает сам.
//
// Ачивка — разовая (условие за партию) или ступень серии (порог метрики: «3 убийства за ход» → «4» → «5»).
// Ступени серии копятся независимо: достиг третьей — получил и первые две. Серия «Победы» — накопительная:
// считается не по партии, а по числу побед в профиле (cumulative). Пороги подобраны по партиям с прода:
// первая ступень — у 5–20% партий людей, последняя — меньше 1%.
//
// События берутся после каждого действия журнала по seq (буфер G.events — последние 400): «Отмена» откатывает
// состояние вместе с событиями, поэтому отменённое тут же отбрасывается — его в партии «не было».
// ACH_VERSION + 1 при любой правке условий или порогов: сервер пересчитает ачивки всех партий.
import type { Game, GameEvent, GameRecord, JournalEntry, PlayerId } from '../types.ts';
import { card, player } from './state.ts';
import { gameFlags, replayJournal, scorePlayer, winnersOf } from './game.ts';
import { matchesOutcome, recPlayers, replayRecordOutcome } from './record.ts';

export const ACH_VERSION = 2;

/** «легенда / легенды / легенд» по числу */
const plural = (n: number, one: string, few: string, many: string): string => {
  const d = n % 10;
  const dd = n % 100;
  return d === 1 && dd !== 11 ? one : d >= 2 && d <= 4 && (dd < 12 || dd > 14) ? few : many;
};

/** Ступень серии: порог и название */
export interface AchTier {
  n: number;
  name: string;
}

/** Серия ачивок с порогами одной метрики */
export interface AchSeries {
  id: string;
  icon: string;
  /** о чём серия (заголовок в профиле) */
  title: string;
  /** условие ступени с порогом n */
  text: (n: number) => string;
  tiers: AchTier[];
  /** накопительная (по профилю, а не по партии): «Победы» — по числу побед, «Охотник на боссов» — по числу разных
   *  побеждённых боссов; сервер её не хранит, ступени считает клиент (cumulativeTiers) */
  cumulative?: boolean;
}

/** Разовая ачивка */
export interface AchSingle {
  id: string;
  icon: string;
  name: string;
  text: string;
}

export const ACH_SERIES: readonly AchSeries[] = [
  { id: 'wins', icon: '🏆', title: 'Победы', text: (n) => (n === 1 ? 'Победи в партии' : `Победи в ${n} партиях`), cumulative: true,
    tiers: [{ n: 1, name: 'Первая победа' }, { n: 10, name: 'Колдун со стажем' }, { n: 50, name: 'Матёрый колдун' }, { n: 100, name: 'Великий колдун' }] },
  { id: 'bosses', icon: '👑', title: 'Охотник на боссов', text: (n) => (n === 1 ? 'Победи в партии с боссом' : n === 10 ? 'Победи всех десятерых боссов' : `Победи ${n} разных боссов`), cumulative: true,
    tiers: [{ n: 1, name: 'Первый босс' }, { n: 5, name: 'Гроза боссов' }, { n: 10, name: 'Все десять' }] },
  { id: 'kills-turn', icon: '💀', title: 'Убийства за ход', text: (n) => `Убей ${n} колдунов за один ход`,
    tiers: [{ n: 3, name: 'Падёж колдунов' }, { n: 4, name: 'Массовый падёж' }, { n: 5, name: 'Крутагидон местного разлива' }] },
  { id: 'kills-game', icon: '⚰️', title: 'Убийства за партию', text: (n) => `Убей ${n} колдунов за партию`,
    tiers: [{ n: 5, name: 'Колдуноборец' }, { n: 8, name: 'Гробовщик' }, { n: 10, name: 'Жнец' }] },
  { id: 'dmg-turn', icon: '🪓', title: 'Урон за ход', text: (n) => `Нанеси врагам ${n} урона за один ход`,
    tiers: [{ n: 30, name: 'Отхватили!' }, { n: 50, name: 'Расчленёнка' }, { n: 100, name: 'Ковровая ушатка' }] },
  { id: 'power-turn', icon: '💪', title: 'Мощь за ход', text: (n) => `Набери ${n} мощи за один ход`,
    tiers: [{ n: 30, name: 'Мощный колдунище' }, { n: 50, name: 'Мощь немереная' }, { n: 100, name: 'Ультимативное тронадо' }] },
  { id: 'plays-turn', icon: '🃏', title: 'Карты за ход', text: (n) => `Сыграй ${n} карт за один ход`,
    tiers: [{ n: 15, name: 'Разогнался' }, { n: 20, name: 'Цепная реакция' }, { n: 30, name: 'Бесконечный ход' }] },
  { id: 'buys-turn', icon: '🛒', title: 'Покупки за ход', text: (n) => `Купи ${n} карт за один ход`,
    tiers: [{ n: 5, name: 'Шопинг на барахолке' }, { n: 8, name: 'Барахольщик' }, { n: 10, name: 'Скупил барахолку' }] },
  { id: 'legends-turn', icon: '👑', title: 'Легенды за ход', text: (n) => `Купи ${n} ${plural(n, 'легенду', 'легенды', 'легенд')} за один ход`,
    tiers: [{ n: 3, name: 'Легендарный барыга' }, { n: 4, name: 'Оптовик легенд' }, { n: 5, name: 'Зал легенд' }] },
  { id: 'legends-game', icon: '🏛️', title: 'Легенды за партию', text: (n) => `Закончи партию с ${n} легендами`,
    tiers: [{ n: 10, name: 'Коллекционер легенд' }, { n: 12, name: 'Хранитель легенд' }, { n: 15, name: 'Сам себе легенда' }] },
  { id: 'score-game', icon: '⭐', title: 'ПО за партию', text: (n) => `Набери ${n} ПО за партию`,
    tiers: [{ n: 50, name: 'Полтинник' }, { n: 75, name: 'Звезда Крутагидона' }, { n: 100, name: 'Сотка' }] },
  { id: 'score-turn', icon: '🌠', title: 'ПО за ход', text: (n) => `Прибавь ${n} ПО за один ход`,
    tiers: [{ n: 15, name: 'Звёздный ход' }, { n: 20, name: 'Звездопад' }, { n: 30, name: 'Звёздный дождь' }] },
  { id: 'chaos-turn', icon: '🎲', title: 'Шальная магия за ход', text: (n) => `Сыграй ${n} шальных магий за один ход`,
    tiers: [{ n: 5, name: 'Шальной' }, { n: 8, name: 'Совсем шальной' }, { n: 10, name: 'Шальная бездна' }] },
  { id: 'trash-turn', icon: '🗑️', title: 'Уничтожение карт', text: (n) => `Уничтожь ${n} ${plural(n, 'свою карту', 'свои карты', 'своих карт')} за один ход`,
    tiers: [{ n: 3, name: 'Чистка колоды' }, { n: 5, name: 'Мусорный ветер' }, { n: 7, name: 'Сделка со Жгуном' }] },
  { id: 'chips-turn', icon: '🍟', title: 'Чипсины за ход', text: (n) => `Получи ${n} чипсин за один ход`,
    tiers: [{ n: 8, name: 'Чипсоед' }, { n: 10, name: 'Чипсихоз' }, { n: 15, name: 'Чипсовый магнат' }] },
  { id: 'heal-turn', icon: '🩹', title: 'Лечение за ход', text: (n) => `Накрути ${n} жизней за один ход`,
    tiers: [{ n: 10, name: 'Подлатался' }, { n: 20, name: 'Живучий' }, { n: 30, name: 'Восставший' }] },
  { id: 'defends-game', icon: '🛡️', title: 'Защиты за партию', text: (n) => `Избеги атаки ${n} раз за партию`,
    tiers: [{ n: 10, name: 'Не попал!' }, { n: 15, name: 'Непробиваемый' }, { n: 20, name: 'Неуязвимый' }] },
  { id: 'redirect-kills', icon: '🪞', title: 'Обратно в лицо', text: (n) => (n === 1 ? 'Убей атакующего, перенаправив его атаку в него же' : `Убей так ${n} атакующих за партию`),
    tiers: [{ n: 1, name: 'Обратно в лицо' }, { n: 2, name: 'Зеркальный колдун' }] },
  { id: 'comeback', icon: '🍑', title: 'Камбэк', text: (n) => `Победи, отставая по ходу партии на ${n} ПО`,
    tiers: [{ n: 15, name: 'Восстание из зада' }, { n: 20, name: 'Великое восстание из зада' }, { n: 30, name: 'Чудо Крутагидона' }] },
  { id: 'margin', icon: '🐟', title: 'Отрыв', text: (n) => `Победи с отрывом ${n} ПО`,
    tiers: [{ n: 20, name: 'Ушатал' }, { n: 40, name: 'Разгром' }, { n: 60, name: 'Всех порвал' }] },
  { id: 'viagra', icon: '🍆', title: 'Вялые палочки в плюс', text: (n) => `Закончи партию с ${n} вялыми палочками в плюс (Виагрус)`,
    tiers: [{ n: 4, name: 'Твёрдый' }, { n: 8, name: 'Твёрдый лорд' }, { n: 10, name: 'Виагрус-император' }] },
];

export const ACH_SINGLES: readonly AchSingle[] = [
  { id: 'loser-win', icon: '🤡', name: 'Лошара-триумфатор', text: 'Победи, будучи лошарой' },
  { id: 'circus-win', icon: '🎪', name: 'Цирк уехал', text: 'Победи с бонусом Цирка братьев Лошашных' },
  { id: 'circus-x2', icon: '🎠', name: 'Двойной цирк', text: 'Переверни Цирком братьев Лошашных удвоенный штраф лошары' },
  { id: 'wipe', icon: '☠️', name: 'Тотальный Крутагидон', text: 'Убей всех врагов за один ход (за столом от 4 колдунов)' },
  { id: 'instant', icon: '🪄', name: 'Лососните тунца', text: 'Победи немедленно Палочкой-ушаталочкой' },
  { id: 'pacifist', icon: '🕊️', name: 'Мирный колдун', text: 'Победи, никого не убив' },
  { id: 'few-legends', icon: '🚫', name: 'Без легенд', text: 'Победи, имея не больше 2 легенд' },
  { id: 'no-ddk', icon: '😇', name: 'Жетоны? Не слышал', text: 'Победи без жетонов дохлых колдунов' },
  { id: 'immortal', icon: '🧟', name: 'Неубиваемый', text: 'Не подохни ни разу за партию' },
  { id: 'self-kill', icon: '🤦', name: 'Сам себя ушатал', text: 'Подохни от собственной карты' },
  { id: 'cheaters', icon: '😈', name: 'Обыграл читеров', text: 'Победи, когда все боты за столом — читеры' },
  { id: 'five', icon: '🖐️', name: 'Пятеро в лодке', text: 'Победи в партии на пятерых' },
  { id: 'hair', icon: '🪒', name: 'На волоске', text: 'Победи с отрывом в 1 ПО' },
  { id: 'tie-legends', icon: '📜', name: 'Легенды решают', text: 'Победи при равенстве ПО — за счёт легенд' },
  { id: 'tie-ddk', icon: '🪦', name: 'Меньше всех дох', text: 'Победи при равенстве ПО и легенд — за счёт меньшего числа жетонов дохлых колдунов' },
  { id: 'shared', icon: '🤝', name: 'Боевая ничья', text: 'Раздели победу при полной ничьей: поровну ПО, легенд и жетонов' },
  { id: 'regicide', icon: '🗡️', name: 'Цареубийство', text: 'Убей босса' },
  { id: 'duel-clean', icon: '🥊', name: 'Чистая дуэль', text: 'Победи босса в дуэли, ни разу не подохнув' },
  { id: 'mutant', icon: '🧬', name: 'Мутант', text: 'Победи в партии с мутатором' },
];

/** id ступени серии: «kills-turn-2» — вторая ступень */
export const tierId = (s: AchSeries, i: number): string => `${s.id}-${i + 1}`;

export interface AchInfo {
  id: string;
  icon: string;
  name: string;
  text: string;
  /** серия и номер ступени (с 1) — у ступеней серий */
  series?: AchSeries;
  tier?: number;
}

/** Все ачивки по id: ступени серий и разовые */
export const ACH_INFO: ReadonlyMap<string, AchInfo> = new Map<string, AchInfo>([
  ...ACH_SERIES.flatMap((s) => s.tiers.map((t, i): [string, AchInfo] => [tierId(s, i), { id: tierId(s, i), icon: s.icon, name: t.name, text: s.text(t.n), series: s, tier: i + 1 }])),
  ...ACH_SINGLES.map((a): [string, AchInfo] => [a.id, { ...a }]),
]);

// ---------- метрики партии ----------

/** Метрики хода игрока (его собственного хода) */
interface TurnStats {
  kills: number;
  victims: Set<PlayerId>;
  dmg: number;
  power: number;
  plays: number;
  buys: number;
  legends: number;
  chaos: number;
  trash: number;
  chips: number;
  heal: number;
  /** ПО на начало хода */
  score0: number;
}

/** Метрики игрока за партию: максимумы по его ходам и суммы */
export interface PlayerStats {
  turn: Record<'kills' | 'dmg' | 'power' | 'plays' | 'buys' | 'legends' | 'chaos' | 'trash' | 'chips' | 'heal' | 'score', number>;
  kills: number;
  deaths: number;
  defends: number;
  redirectKills: number;
  selfKills: number;
  /** убил всех врагов за один свой ход */
  wipe: boolean;
  /** наибольшее отставание от лидера по ПО на начало хода */
  deficit: number;
  /** убитых боссов */
  bossKills: number;
}

const TURN_KEYS = ['kills', 'dmg', 'power', 'plays', 'buys', 'legends', 'chaos', 'trash', 'chips', 'heal'] as const;

/** Сборщик метрик: кормится событиями после каждого действия реплея */
class Tracker {
  readonly stats: PlayerStats[];
  private last = -1;
  private cur: { pid: PlayerId; t: TurnStats } | null = null;
  /** перенаправления текущей атаки: кому (атакующему) — кто перенаправил */
  private redirected = new Map<PlayerId, PlayerId>();
  /** последний урон текущей атаки по игроку: от кого (самоповреждение — источник он сам) */
  private lastHit = new Map<PlayerId, PlayerId | null>();
  private readonly G: Game;

  constructor(G: Game) {
    this.G = G;
    this.stats = G.players.map(() => ({
      turn: { kills: 0, dmg: 0, power: 0, plays: 0, buys: 0, legends: 0, chaos: 0, trash: 0, chips: 0, heal: 0, score: 0 },
      kills: 0, deaths: 0, defends: 0, redirectKills: 0, selfKills: 0, wipe: false, deficit: 0, bossKills: 0,
    }));
  }

  step(e: JournalEntry): void {
    const G = this.G;
    if (e.a.name === 'cancel') {
      // откат: события отменённого действия ушли из G.events вместе с состоянием — дальше счёт с восстановленного seq
      this.last = G.events.length ? G.events[G.events.length - 1]!.seq : -1;
      return;
    }
    for (const ev of G.events) if (ev.seq > this.last) {
      this.last = ev.seq;
      this.event(ev);
    }
  }

  private closeTurn(scoreNow: number[]): void {
    if (!this.cur) return;
    const { pid, t } = this.cur;
    const s = this.stats[pid]!;
    for (const k of TURN_KEYS) s.turn[k] = Math.max(s.turn[k], t[k]);
    s.turn.score = Math.max(s.turn.score, (scoreNow[pid] ?? 0) - t.score0);
    const enemies = this.G.players.filter((p) => p.id !== pid);
    if (this.G.players.length >= 4 && enemies.every((p) => t.victims.has(p.id))) s.wipe = true;
    this.cur = null;
  }

  private scores(): number[] {
    return this.G.players.map((p) => scorePlayer(this.G, p).total);
  }

  private event(ev: GameEvent): void {
    const t = this.cur?.t;
    const me = this.cur?.pid;
    switch (ev.type) {
      case 'turn': {
        const sc = this.scores();
        this.closeTurn(sc);
        const lead = Math.max(...sc);
        sc.forEach((v, i) => { this.stats[i]!.deficit = Math.max(this.stats[i]!.deficit, lead - v); });
        this.cur = { pid: ev.pid, t: { kills: 0, victims: new Set(), dmg: 0, power: 0, plays: 0, buys: 0, legends: 0, chaos: 0, trash: 0, chips: 0, heal: 0, score0: sc[ev.pid] ?? 0 } };
        break;
      }
      case 'play':
        if (t && ev.pid === me) { t.plays++; if (ev.cid === 'shalnaya-magiya') t.chaos++; }
        break;
      case 'power': if (t && ev.pid === me && ev.n > 0) t.power += ev.n; break;
      case 'buy':
        if (t && ev.pid === me) { t.buys++; if (card(ev.cid).legend) t.legends++; }
        break;
      case 'chips': if (t && ev.pid === me && ev.n > 0) t.chips += ev.n; break;
      case 'heal': if (t && ev.pid === me) t.heal += ev.n; break;
      case 'trash': if (t && ev.pid === me) t.trash++; break;
      case 'attack': this.redirected.clear(); this.lastHit.clear(); break;
      case 'redirect': this.redirected.set(ev.to, ev.from); break;
      case 'defend': this.stats[ev.pid]!.defends++; break;
      case 'damage':
        this.lastHit.set(ev.pid, ev.from);
        if (t && ev.from === me && ev.pid !== me) t.dmg += ev.n;
        break;
      case 'die': {
        this.stats[ev.pid]!.deaths++;
        // перенаправленная атака убила атакующего: убийца — перенаправивший (с v16 так и в событии; в старых
        // партиях killer пуст — засчитываем по перенаправлению)
        const by = this.redirected.get(ev.pid);
        const killer = ev.killer ?? (by != null ? by : null);
        if (by != null && killer === by) this.stats[by]!.redirectKills++;
        else if (killer == null && this.lastHit.get(ev.pid) === ev.pid) this.stats[ev.pid]!.selfKills++;
        if (killer != null && killer !== ev.pid) {
          this.stats[killer]!.kills++;
          if (this.G.players[ev.pid]?.boss) this.stats[killer]!.bossKills++;
          if (t && killer === me) { t.kills++; t.victims.add(ev.pid); }
        }
        this.lastHit.delete(ev.pid);
        break;
      }
      case 'end': this.closeTurn(this.scores()); break;
      default: break;
    }
  }
}

/** Ачивки одного игрока по метрикам и итогу партии */
function earned(G: Game, pid: PlayerId, s: PlayerStats): string[] {
  const out: string[] = [];
  const p = player(G, pid);
  const scores = G.scores ?? [];
  const mine = scores.find((x) => x.pid === pid);
  if (!mine) return out;
  const winners = winnersOf(G);
  const won = winners.includes(pid);
  // отрыв победителя — от лучшего из непобедителей (при полной ничьей другие победители не в счёт)
  const others = scores.filter((x) => !winners.includes(x.pid));
  const margin = mine.total - (others.length ? Math.max(...others.map((x) => x.total)) : mine.total);
  const viagra = Math.max(0, ...mine.lines.map((l) => /^Вялые палочки ×(\d+) \(в плюс\)/.exec(l.label)).map((m) => (m ? Number(m[1]) : 0)));
  const metric: Record<string, number> = {
    'kills-turn': s.turn.kills, 'kills-game': s.kills, 'dmg-turn': s.turn.dmg, 'power-turn': s.turn.power,
    'plays-turn': s.turn.plays, 'buys-turn': s.turn.buys, 'legends-turn': s.turn.legends, 'legends-game': mine.legends,
    'score-game': mine.total, 'score-turn': s.turn.score, 'chaos-turn': s.turn.chaos, 'trash-turn': s.turn.trash,
    'chips-turn': s.turn.chips, 'heal-turn': s.turn.heal, 'defends-game': s.defends, 'redirect-kills': s.redirectKills,
    comeback: won ? s.deficit : 0, margin: won ? margin : 0, viagra,
  };
  for (const ser of ACH_SERIES) {
    if (ser.cumulative) continue;
    const v = metric[ser.id] ?? 0;
    ser.tiers.forEach((tr, i) => { if (v >= tr.n) out.push(tierId(ser, i)); });
  }
  const bots = G.players.filter((x) => x.bot);
  const second = scores.find((x) => x.pid !== pid && !winners.includes(x.pid));
  const single = (id: string, ok: boolean): void => { if (ok) out.push(id); };
  single('loser-win', won && p.loser);
  single('circus-win', won && mine.lines.some((l) => l.label.startsWith('Цирк')));
  single('circus-x2', mine.lines.some((l) => l.label.startsWith('Цирк') && l.label.includes('×2')));
  single('wipe', s.wipe);
  single('instant', won && G.instantWin === pid);
  single('pacifist', won && s.kills === 0);
  single('few-legends', won && mine.legends <= 2);
  single('no-ddk', won && mine.ddk === 0);
  single('immortal', s.deaths === 0);
  single('self-kill', s.selfKills > 0);
  single('cheaters', won && bots.length > 0 && bots.every((b) => b.botLevel === 'cheat'));
  single('five', won && G.players.length >= 5);
  single('hair', won && winners.length === 1 && margin === 1);
  single('tie-legends', won && winners.length === 1 && G.instantWin == null && !!second && second.total === mine.total && second.legends < mine.legends);
  single('tie-ddk', won && winners.length === 1 && G.instantWin == null && !!second && second.total === mine.total && second.legends === mine.legends && second.ddk > mine.ddk);
  single('shared', won && winners.length > 1);
  const muts = G.mutators ?? [];
  single('regicide', s.bossKills > 0);
  single('duel-clean', won && muts.includes('duel') && s.deaths === 0);
  single('mutant', won && muts.some((m) => m !== 'duel'));
  return out;
}

type Rec = Pick<GameRecord, 'seed' | 'players' | 'journal'> & Partial<Pick<GameRecord, 'rebalance' | 'mutators' | 'rebalanceVersion' | 'rulesVersion' | 'winner' | 'winners' | 'scores'>> & { v?: number };

export interface GameAchievements {
  /** реплей лёг целиком, и итог совпал с записанным */
  ok: boolean;
  error: string;
  /** ачивки людей по местам за столом (боты не получают) */
  players: { pid: PlayerId; ids: string[] }[];
  /** версия правил, на которой запись воспроизвелась (сервер запоминает подобранную для записей без версии) */
  version?: number;
}

/**
 * Ачивки партии по записи: реплей журнала на версии правил записи (не записана — подбирается) со сбором метрик.
 * Запись, которая не воспроизводится или чей итог не совпал, ачивок не даёт (ok: false).
 */
export function gameAchievements(rec: Rec): GameAchievements {
  let version = rec.rulesVersion;
  if (version == null) {
    const o = replayRecordOutcome(rec);
    if (!o.ok || o.switched) return { ok: false, error: o.error || 'версия правил не подобрана', players: [] };
    version = o.version;
  }
  let tr: Tracker | null = null;
  const spec = { seed: rec.seed, players: recPlayers(rec), ...gameFlags(rec), journal: rec.journal };
  const r = replayJournal(spec, version, Infinity, (G, e) => { (tr ??= new Tracker(G)).step(e); });
  if (r.failAt >= 0) return { ok: false, error: r.error || `журнал не лёг на записи ${r.failAt}`, players: [] };
  if (!matchesOutcome(r.G, rec)) return { ok: false, error: 'итог партии не совпал с записью', players: [] };
  const G = r.G;
  if (G.phase !== 'ended' || !tr) return { ok: true, error: '', players: [], version };
  const stats = (tr as Tracker).stats;
  return { ok: true, error: '', version, players: G.players.filter((p) => !p.bot).map((p) => ({ pid: p.id, ids: earned(G, p.id, stats[p.id]!) })) };
}

/** Ступени накопительной серии id, достигнутые при значении n */
export function cumulativeTiers(id: 'wins' | 'bosses', n: number): string[] {
  const s = ACH_SERIES.find((x) => x.id === id)!;
  return s.tiers.flatMap((t, i) => (n >= t.n ? [tierId(s, i)] : []));
}

/** Ступени серии «Победы», достигнутые при wins победах */
export const winTiers = (wins: number): string[] => cumulativeTiers('wins', wins);
